import customParseFormat from 'dayjs/plugin/customParseFormat';
import dayjs from 'dayjs';
import { areEmployerStepsRequired } from 'utils/employerClient';
import { EMAIL_REGEXP } from '../constants';
import translate from 'utils/translate';
import { EmploymentStatus } from 'hooks/webapi/useClient';

dayjs.extend(customParseFormat);

export const CZECH_DIACRITICS_LOWERCASE = [
  '\u00E1',
  '\u010D',
  '\u010F',
  '\u00E9',
  '\u011B',
  '\u00ED',
  '\u0148',
  '\u00F3',
  '\u0159',
  '\u0161',
  '\u0165',
  '\u00FA',
  '\u016F',
  '\u00FD',
  '\u017E',
  '\u00C1',
  '\u010C',
  '\u010E',
  '\u00C9',
  '\u011A',
  '\u00CD',
  '\u0147',
  '\u00D3',
  '\u0158',
  '\u0160',
  '\u0164',
  '\u00DA',
  '\u016E',
  '\u00DD',
  '\u017D',
];

const SAFE_NAME_REGEXP = /^[^±!@£$%^&*_+§¡€#¢§¶•ªº«\\\\/<>?:;|=.,]{1,64}$/i;

export const CZ_PHONE_REGEXP =
  /^(\+?420)?(6([0-9][1-9]|[1-9][0-9])|7[0-9]{2})[0-9]{6}$/;

export const ONLY_LETTERS = /^[\p{L}\p{M}.]+$/u;

type ValidationFunc = (value: any) => string | undefined;

// Runs all validators against value and joins non-empty errors with '-divider-' sentinel
export function applyAll(...validations: Array<ValidationFunc>) {
  return (value: any) => {
    const errors = validations.reduce((acc, cur) => {
      const err = cur(value);
      if (err) {
        return `${err}-divider-${acc}`;
      }
      return acc;
    }, '');

    return errors.length > 0 ? errors : undefined;
  };
}

// Treats false and explicit null/empty string as empty; 0 and true are considered non-empty
export const isEmpty = (
  value: string | null | undefined | number | boolean,
): boolean => {
  if (typeof value === 'boolean') {
    if (!value) {
      return true;
    }
    return false;
  }

  if (value || value === 0) {
    if (String(value) === 'null' || String(value).length === 0) {
      return true;
    }
  } else {
    return true;
  }

  return false;
};

export const negativeNumber = (value: string | null | undefined) => {
  if (Number(value) < 0) {
    return translate('error.validation.negative_number');
  }
  return undefined;
};

export const notANumber = (value: string | null | undefined) => {
  if (!/^-?\d+\.?\d*$/.test(String(value))) {
    return translate('error.validation.not_a_number');
  }
  return undefined;
};

export const lessThanOne = (value: string | null | undefined) => {
  if (value === undefined || value === null) {
    return translate('error.validation.less_than_one');
  }

  if (parseInt(value, 10) < 1 || notANumber(value)) {
    return translate('error.validation.less_than_one');
  }

  return null;
};

export const empty = (value: string | null | undefined | boolean) => {
  if (isEmpty(value)) {
    return translate('error.validation.empty');
  }
  return undefined;
};

export const tooShort = (value: string | null | undefined) => {
  if (typeof value === 'undefined' || String(value).trim().length < 2) {
    return translate('error.validation.too_short');
  }
  return undefined;
};

export const minLength =
  (length: number) => (value: string | null | undefined) => {
    if (typeof value === 'undefined' || String(value).trim().length < length) {
      return translate('error.validation.minLength', { length });
    }
    return undefined;
  };

export const maxLength =
  (length: number, subject?: string) => (value?: string | null) => {
    if (typeof value === 'undefined' || String(value).trim().length > length) {
      return subject
        ? translate('error.validation.max_length_with_subject', {
            length,
            subject,
          })
        : translate('error.validation.max_length', { length });
    }
    return undefined;
  };

export const requiresNumber = (value: string | null | undefined) => {
  const hasNumberRegex = /\d/;
  if (value && !hasNumberRegex.test(value)) {
    return translate('error.validation.requires_number');
  }
  return undefined;
};

export const notSafeName = (
  value: string | null | undefined,
): string | undefined => {
  if (!SAFE_NAME_REGEXP.test(String(value))) {
    return translate('error.validation.not_safe_name');
  }
  return undefined;
};

export const noDigitsAllow = (
  value: string | null | undefined,
): string | undefined => {
  // only letters allowed
  if (!ONLY_LETTERS.test(String(value))) {
    return translate('error.validation.invalid_format');
  }
  return undefined;
};

export const notPhoneNumber = (
  value: string | null | undefined,
): string | undefined => {
  if (!CZ_PHONE_REGEXP.test(String(value))) {
    return translate('error.validation.not_phone_number');
  }
  return undefined;
};

export const employerValidation =
  (employmentStatus: EmploymentStatus | undefined) =>
  (value: EmploymentStatus | string | undefined) => {
    if (areEmployerStepsRequired(employmentStatus)) return empty(value);
    return undefined;
  };

export const notEmail = (
  value: string | null | undefined,
): string | undefined => {
  if (!EMAIL_REGEXP.test(String(value))) {
    return translate('error.validation.not_email');
  }

  return undefined;
};

export const birthNumberInvalid = (
  value: string | null | undefined,
): string | undefined => {
  const FEMALE_MARKER = 50;
  const DATE_FORMAT = 'YY/M/DD';
  const YEAR_OFFSET = 54;
  const BORN_BEFORE_54_LENGTH = 10;
  const BORN_AFTER_54_LENGTH = 11;
  const PERSONAL_ID_PATTERN_REGEXP = /\d{6}\/\d{3,4}/;
  const LIMITED_AGE = 78; // '#CWT-3001'

  if (value) {
    if (
      value.length >= BORN_BEFORE_54_LENGTH &&
      value.length <= BORN_AFTER_54_LENGTH
    ) {
      const year = parseInt(value.substring(0, 2), 10);
      let month = parseInt(value.substring(2, 4), 10);

      const day = value.substring(4, 6);
      const fullDate = new Date(`${year}-${month}-${parseInt(day, 10)}`);

      if (month > FEMALE_MARKER) {
        month -= FEMALE_MARKER;
      }

      const date = dayjs(
        `${value.substring(0, 2)}/${month}/${day}`,
        DATE_FORMAT,
        true,
      );
      const allowedLength =
        year < YEAR_OFFSET
          ? value.length === BORN_BEFORE_54_LENGTH ||
            value.length === BORN_AFTER_54_LENGTH
          : value.length === BORN_AFTER_54_LENGTH;

      // Verifies the 10th digit is the remainder of the 9-digit number mod 11 (remainder 10 maps to 0)
      const invalidControlSum = (personalId: string): boolean => {
        const shortId = personalId.replace('/', '');
        const checkDigit = parseInt(shortId.substring(9), 10);
        const number = parseInt(shortId.substring(0, 9), 10);
        let control = number % 11;
        if (control === 10) {
          control = 0;
        }
        return control !== checkDigit;
      };

      // Returns undefined (no error) only when the applicant is 18–77 years old
      const underMaximumAgeLimit = (): boolean | undefined => {
        const ageDifMs = Date.now() - fullDate.getTime();
        const ageDate = new Date(ageDifMs);
        const age = Math.abs(ageDate.getUTCFullYear() - 1970);

        if (age < LIMITED_AGE && age >= 18) {
          return false;
        }

        return undefined;
      };

      if (!PERSONAL_ID_PATTERN_REGEXP.test(String(value))) {
        return translate('error.validation.personal_id_doesnt_match_pattern');
      }

      if (!allowedLength) {
        return translate('error.validation.personal_id_wrong_length');
      }

      if (value.length === BORN_AFTER_54_LENGTH && invalidControlSum(value)) {
        return translate('error.validation.personal_id_wrong_checksum');
      }

      if (month > 12) {
        return translate('error.validation.personal_id_month_invalid');
      }

      if (!date.isValid()) {
        return translate('error.validation.personal_id_date_invalid');
      }

      if (parseInt(value, 10) < 18) {
        return translate('error.validation.too_young');
      }

      if (underMaximumAgeLimit()) {
        return translate('error.validation.too_old');
      }
    } else {
      return translate('error.validation.personal_id_invalid');
    }
  } else {
    return translate('error.validation.personal_id_empty');
  }

  return undefined;
};

// Normalises a Czech bank account into `PPPPPP-NNNNNNNNNNCCCC` form (prefix padded to 6 digits)
export const getFullBankAccount = (bankAccount: string): string => {
  const hasPrefix = bankAccount.includes('-');
  const bankAccountWithPrefix = bankAccount.split('-');
  const prefix = hasPrefix
    ? bankAccountWithPrefix[0].padStart(6, '0')
    : '000000';
  const accountNumberWithCode = hasPrefix
    ? bankAccountWithPrefix[1].split('/')
    : bankAccount.split('/');
  const accountNumber = accountNumberWithCode[0];
  const bankCode = accountNumberWithCode[1];

  if (!bankCode) throw new Error('missing_bank_code');

  return `${prefix}-${accountNumber}${bankCode}`;
};

// Applies CZ bank-account mod-11 weights; returns true when the weighted digit sum is divisible by 11
const validateControlDigit = (fullBankAccount: string): boolean => {
  const MOD_NUMBERS = [10, 5, 8, 4, 2, 1, 0, 6, 3, 7, 9, 10, 5, 8, 4, 2, 1];
  let checkSum = 0;

  for (let i = 0; i < MOD_NUMBERS.length; i += 1) {
    const char = fullBankAccount[i];
    if (!notANumber(char)) {
      checkSum += MOD_NUMBERS[i] * (Number(char) - 48);
    }
  }
  const control = checkSum % 11;

  return control === 0;
};

export const bankAccountInvalid = (value: string): string | undefined => {
  const ACCOUNT_PATTERN = /^(\d{0,6}-)?\d{2,10}\/\d{4}$/;
  let fullBankAccount = '';
  try {
    fullBankAccount = getFullBankAccount(value);
  } catch (e) {
    console.error(e);
    return translate('error.validation.bank_account');
  }

  if (!ACCOUNT_PATTERN.test(value) || validateControlDigit(fullBankAccount)) {
    return translate('error.validation.bank_account');
  }

  return undefined;
};

export const idCardInvalid = (value: string): string | undefined => {
  const MIN_LENGTH = 9;
  const MAX_LENGTH = 10;
  const ID_CARD_PATTERN = new RegExp(
    `^[0-9a-zA-Z]{${MIN_LENGTH},${MAX_LENGTH}}$`,
  );

  if (!ID_CARD_PATTERN.test(value)) {
    return translate('error.validation.id_card_invalid');
  }

  return undefined;
};

export const postalCodeInvalid = (value: string): string | undefined => {
  const POSTAL_CODE_PATTERN = /^(\d{3}(\s)?\d{2})$/;
  if (!POSTAL_CODE_PATTERN.test(value)) {
    return translate('error.validation.postal_code_invalid');
  }

  return undefined;
};

export const houseNumberInvalid = (value: string): string | undefined => {
  // Ex. 902/11b
  const HOUSE_NUMBER_PATTERN = /^(([1-9][0-9]*)\/)?([1-9][0-9]*[a-zA-Z]?)$/;
  if (!HOUSE_NUMBER_PATTERN.test(value)) {
    return translate('error.validation.house_number_invalid');
  }

  return undefined;
};

export const hasUppercase = (value: string | null | undefined) => {
  if (value && !/[A-Z]/g.test(value)) {
    return translate('error.validation.has_uppercase');
  }

  return undefined;
};

export const hasLowercase = (value: string | null | undefined) => {
  if (value && !/[a-z]/g.test(value)) {
    return translate('error.validation.has_lowercase');
  }

  return undefined;
};

/** blocks completely the use of diacritics characters, used for password validation */
export const restrictDiacriticsChars = (
  value: string | null | undefined,
): string | undefined => {
  const DIACRITICS_REGEX = /[À-ž]/;

  if (value && DIACRITICS_REGEX.test(value)) {
    return translate('error.validation.restrict_diacritics_chars');
  }

  return undefined;
};

export const rangeValue =
  (minValue: number, maxValue: number, validText?: string) =>
  (value?: string | null) => {
    if (Number(value) > maxValue || Number(value) < minValue) {
      return validText || translate('error.validation.out_of_range');
    }
    return undefined;
  };

export const firstNumLarger =
  (firstValue: number, secondValue: number, validText: string) => () => {
    if (firstValue > secondValue) {
      return validText;
    }
    return undefined;
  };

export function validateMaxFileSize(
  blob: Blob | null,
  errorMessage: string,
  maxFileSizeKB: number,
) {
  if (!blob) {
    return undefined;
  }

  return blob.size / 1024 > maxFileSizeKB ? errorMessage : undefined;
}

export function validateAllowedFileType(
  blob: Blob | null,
  errorMessage: string,
  allowedTypes: string[],
) {
  if (!blob) {
    return undefined;
  }

  return allowedTypes.includes(blob.type) ? undefined : errorMessage;
}

export function validateExists(blob: Blob | null, errorMessage: string) {
  if (!blob) {
    return errorMessage;
  }

  return undefined;
}

export const validateMaxSize = (blob: Blob | null, maxSize = 10240) =>
  validateMaxFileSize(
    blob,
    translate('error.validation.document_file_size'),
    maxSize,
  );

export const validateFileType = (blob: Blob | null) =>
  validateAllowedFileType(
    blob,
    translate('error.validation.document_file_type'),
    ['image/png', 'image/jpeg', 'application/pdf'],
  );

export const validateNonEmpty = (blob: Blob | null) =>
  validateExists(blob, translate('error.validation.empty'));

export const hasAlphanumericChars = (subject?: string) => (value?: string) => {
  const IS_CHAR = /[À-ža-zA-Z]/g;
  const IS_NUMERIC = /[0-9]/g;

  if (
    value &&
    value.match(IS_CHAR)?.length &&
    value.match(IS_NUMERIC)?.length
  ) {
    return;
  }

  return subject
    ? translate('error.validation.has_alphanumeric_chars_with_subject', {
        subject,
      })
    : translate('error.validation.has_alphanumeric_chars');
};

export const hasSpaces = (value?: string | null): string | undefined => {
  // Password must not contain any kind of whitespaces
  const HAS_SPACES_REGEX = /\s/;

  if (value && HAS_SPACES_REGEX.test(value)) {
    return translate('error.validation.password_contains_space');
  }
  return undefined;
};

export const restrictSpecialChars = (value?: string) => {
  const SPECIAL_CHAR_REGEX = /[^À-ža-zA-Z0-9-_\s]/;

  if (value && SPECIAL_CHAR_REGEX.test(value)) {
    return translate('error.validation.restrict_special_chars');
  }
  return undefined;
};
